import tenantConstants from '@constants';
import TenantComponents from '@components';
import tenantRoutes from '@routes';
import { Col, Row, Typography, Badge } from 'antd';
import cx from 'clsx';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Card, Flex, Heading, Icon, notification } from '../../../../components/common';
import { useRouteNavigate } from '../../../../hooks';
import OtpVerificationModal from '../../../../components/otp-verification-modal/otp-verification-modal';
import {
  useSendLicenseOtpMutation,
  useVerifyLicenseOtpMutation,
  usePostNewListingMutation,
} from '../../../../apis/postlisting';
import { useSearchParams } from 'react-router-dom';
import {
  ChooserGrid,
  ChooserHeader,
  ChooserSubtitle,
  DirectionalChevron,
  NewRibbon,
  NewRibbonFold,
  OptionAction,
  OptionBody,
  OptionCard,
  OptionCardHead,
  OptionChevron,
  OptionDescription,
  OptionFooter,
  OptionFooterRow,
  OptionIconBox,
  OptionMeta,
  OptionTitle,
  PropertyCard,
} from '../../../../container/pages/post-listing/styled';
import {
  addPropertyEvent,
  customEventClick,
  regaAdValidationEvent,
  selectListingTypeClickEvent,
} from '../../../../services/analyticsService';
import { PAGE_SECTIONS } from '../../../../services/analyticsService/constants';
import {
  POST_LISTING_OPTION_KEYS,
  POST_LISTING_STEP_PARAM,
  POST_LISTING_STEPS,
  getPostListingCardCopy,
  getPostListingOptions,
} from '../../../../components/post-listing-menu/post-listing-options';
import { getBaseURL, getClassifiedBaseURL } from '../../../../utility/env';
import LimitNonSaudiNationalModal from '../../../../components/limitNonSaudiNationalModal/LimitNonSaudiNationalModal';
const { Text, Title } = Typography;
import PermitValidation from './permit-validation';
import { getLocaleForURL } from '../../../../utility/language';
const cardList = [
  {
    id: 1,
    icon: 'PropertyIcon',
    propertyType: 'Sell or Rent Property',
    description: 'Find buyers and tenants for your property',
  },
  {
    id: 2,
    icon: 'RentalIcon',
    propertyType: 'Daily Rentals',
    description: 'Host guests at your place for short stays',
  },
];

const PostListingWithPropertyType = () => {
  const { t } = useTranslation();
  const isFalOtpVerificationEnabled = tenantConstants?.ENABLE_FAL_LICENSE_OTP_VERIFICATION;
  const [selectedCardId, setSelectedCardId] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAdLicenseField, setShowAdLicenseField] = useState(false);
  const [showPermitField, setShowPermitField] = useState(false);
  const navigate = useRouteNavigate();
  const otpVerifyRef = useRef();
  const [sendLicenseOtp] = useSendLicenseOtpMutation();
  const [verifyLicenseOtp] = useVerifyLicenseOtpMutation();
  const [otpListingId, setOtpListingId] = useState(null);
  const [otpPhoneNumber, setOtpPhoneNumber] = useState(null);
  const [otpReferenceNumber, setOtpReferenceNumber] = useState(null);
  const [hasRateLimitError, setHasRateLimitError] = useState(false);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const { user } = useSelector((state) => state.app.loginUser);
  const [postNewListing, _] = usePostNewListingMutation();
  const enablePostListingRevamp = tenantConstants?.ENABLE_POST_LISTING_REVAMP;
  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get(POST_LISTING_STEP_PARAM);
  const options = getPostListingOptions({ t });
  const cardCopy = getPostListingCardCopy({ t });

  // With the revamp on, the step lives in the URL so Back returns to the card grid instead of
  // leaving the page, and the two form flags are derived from it rather than held separately.
  const showAdLicense = enablePostListingRevamp ? step === POST_LISTING_STEPS.LICENSE_NUMBER : showAdLicenseField;
  const showPermit = enablePostListingRevamp ? step === POST_LISTING_STEPS.PERMIT_NUMBER : showPermitField;

  const goToStep = (nextStep, { replace = false } = {}) => {
    const params = new URLSearchParams(searchParams);
    if (nextStep) params.set(POST_LISTING_STEP_PARAM, nextStep);
    else params.delete(POST_LISTING_STEP_PARAM);
    setSearchParams(params, { replace });
  };

  /**
   * Leaving the chooser goes back to wherever the user came in from. A Lite user has no dashboard
   * — appRoutes only registers that route for Package users — so sending them there lands on a
   * blank page. My Listings is the member-area equivalent.
   */
  const exitChooserPath = user?.is_package_user
    ? tenantRoutes.app().dashboard.path
    : tenantRoutes.app().listings.path;

  // Override 7 — on desktop a Package user skips the card grid, because the nav menu already
  // offered the three options. Mobile has no menu, so a Package user gets the same three-card
  // page as a Lite user and must not be redirected past it.
  useEffect(() => {
    if (!enablePostListingRevamp || isMobile || showAdLicense || showPermit) return;
    if (user?.is_package_user) goToStep(POST_LISTING_STEPS.LICENSE_NUMBER, { replace: true });
  }, [enablePostListingRevamp, isMobile, showAdLicense, showPermit, user?.is_package_user]);

  /**
   * Returning to the grid replaces the step instead of pushing, so leaving a form does not stack
   * a second /post-listing entry. Without this the page's own Back — navigate(-1) — walks into
   * the step just left rather than out to wherever the user came from.
   */
  const handleSetShowAdLicenseField = (value) => {
    if (!enablePostListingRevamp) return setShowAdLicenseField(value);
    if (!value) goToStep(null, { replace: true });
  };

  const handleSetShowPermitField = (value) => {
    if (!enablePostListingRevamp) return setShowPermitField(value);
    if (!value) goToStep(null, { replace: true });
  };

  const handleOptionSelect = (option) => {
    addPropertyEvent(user, tenantRoutes.app().post_listing.path.split('/').pop(), false, {
      pageSection: PAGE_SECTIONS.MEMBER_AREA_CHOOSER,
      name: option.analyticsName,
    });

    if (option.key === POST_LISTING_OPTION_KEYS.DAILY_RENTAL) {
      // Portfolio Lite daily rentals leave Profolio for the classifieds host flow, exactly as the
      // Continue handler does today. Keyed on daily_rental, never a card index.
      if (!user?.is_package_user && tenantConstants?.NAVIGATE_TO_ADD_PROPERTY) {
        const localePath = getLocaleForURL();
        window.location.href = `${getClassifiedBaseURL()}${localePath}/stays/become-host/`;
        return;
      }
      goToStep(POST_LISTING_STEPS.PERMIT_NUMBER);
      return;
    }

    if (option.key === POST_LISTING_OPTION_KEYS.REQUEST_AD_LICENSE) {
      navigate(option.path);
      return;
    }

    goToStep(POST_LISTING_STEPS.LICENSE_NUMBER);
  };

  const getSelectedListingType = (id) => {
    return cardList.find((item) => item.id === id)?.propertyType;
  };

  const handleCardClick = (id) => {
    setSelectedCardId(id);
    customEventClick(user, {
      itemId: `property_card_${id}`,
      additionalInfo: getSelectedListingType(id),
    });
  };
  const postNewListings = async (values, onResponseSuccess = () => { }) => {
    setLoading(true);
    setHasRateLimitError(false);
    const response = await postNewListing(values);
    if (response) {
      onResponseSuccess(response);
      setLoading(false);
      if (response?.error) {
        notification.error(response?.error?.errorMessage || response?.error?.message || t('Something went wrong'));
      } else {
        const listingId = response?.data?.listing?.id;
        if (isFalOtpVerificationEnabled && response?.data?.is_otp_required) {
          const sendRes = await sendLicenseOtp({
            listing_id: listingId,
            user_id: user?.id,
            identifier: response?.data?.phone_number,
          });
          if (sendRes?.error) {
            notification.error(sendRes?.error);
          } else {
            setOtpReferenceNumber(sendRes?.data?.reference_number);
            setOtpListingId(listingId);
            setOtpPhoneNumber(response?.data?.phone_number);
            otpVerifyRef?.current?.showModal('mobile');
          }
        } else {
          navigate(`${tenantRoutes.app().post_listing.path}/${listingId}`);
          !values?.is_daily_rental && regaAdValidationEvent(user);
        }
      }
    }
  };
  const handleClickOnListingSelection = () => {
    customEventClick(user, {
      itemId: selectedCardId,
      additionalInfo: getSelectedListingType(selectedCardId), // Send additional info
    });
    selectListingTypeClickEvent(user, getSelectedListingType(selectedCardId));

    if (!user?.is_package_user && tenantConstants?.NAVIGATE_TO_ADD_PROPERTY && selectedCardId === 2) {
      const localePath = getLocaleForURL();
      const baseUrl = `${getClassifiedBaseURL()}${localePath}/stays/become-host/`;
      window.location.href = baseUrl;
      return;
    }

    if (selectedCardId == 2) {
      setShowPermitField(true);
    } else {
      setShowAdLicenseField(true);
    }
  };
  const renderOptionCard = (option) => {
    const copy = cardCopy[option.key];
    const iconBox = (
      <OptionIconBox $isMobile={isMobile}>
        <Icon icon={option.icon} size="44px" />
      </OptionIconBox>
    );
    const body = (
      <OptionBody>
        <OptionTitle $isMobile={isMobile}>{option.label}</OptionTitle>
        <OptionDescription $isMobile={isMobile}>{copy.description}</OptionDescription>
      </OptionBody>
    );

    return (
      <OptionCard key={option.key} type="button" $isMobile={isMobile} onClick={() => handleOptionSelect(option)}>
        {option.isNew && (
          <NewRibbon $isMobile={isMobile}>
            {t('NEW')}
            {!isMobile && <NewRibbonFold />}
          </NewRibbon>
        )}
        {isMobile ? (
          <OptionCardHead>
            {iconBox}
            {body}
            <OptionChevron>
              <DirectionalChevron size={18} />
            </OptionChevron>
          </OptionCardHead>
        ) : (
          <>
            {iconBox}
            {body}
          </>
        )}
        <OptionFooter $isMobile={isMobile}>
          <OptionFooterRow $isMobile={isMobile}>
            <OptionMeta $isMobile={isMobile}>{copy.meta}</OptionMeta>
            {!isMobile && (
              <OptionAction>
                {copy.action}
                <DirectionalChevron size={14} />
              </OptionAction>
            )}
          </OptionFooterRow>
        </OptionFooter>
      </OptionCard>
    );
  };

  const renderPropertyCards = (selectedCard) => {
    return (
      <PropertyCard
        style={{ borderWidth: 1, '--ant-padding-lg': isMobile ? '12px' : '24px' }}
        key={selectedCard.id}
        onClick={() => handleCardClick(selectedCard.id)}
        className={cx('pointer ', selectedCard.id === selectedCardId && 'active-card')}
      >
        <Flex align="start" vertical={!isMobile && true} gap="8px">
          <Icon icon={selectedCard.icon} iconProps={{ size: isMobile ? '36px' : '52px' }} />
          <div>
            <Title
              className="mb-0"
              level={5}
              style={{ '--ant-font-weight-strong': '700', fontSize: isMobile ? '14px' : '18px' }}
            >
              {t(selectedCard.propertyType)}
            </Title>
            <Text className={!isMobile && 'fz-16'} type="secondary">
              {t(selectedCard.description)}
            </Text>
          </div>
        </Flex>
      </PropertyCard>
    );
  };

  return (
    <>
      <LimitNonSaudiNationalModal actionSource="upgrade" />

      {!showAdLicense && !showPermit ? (
        enablePostListingRevamp ? (
          // Matching margins top and bottom: the cards are the last thing on the page, and with
          // no bottom margin the card butts straight into the footer, which the listings page
          // never does.
          <Row style={{ marginBlock: '20px' }}>
            <Col xs={24} style={{ maxWidth: '1000px', margin: 'auto' }}>
              <Button
                size="small"
                className="mb-8"
                style={{ border: 0 }}
                type="primaryOutlined"
                icon="IoMdArrowRoundBack"
                onClick={() => navigate(exitChooserPath)}
                iconSize="14px"
              >
                {' '}
                {t('Back')}
              </Button>
              <Card bodyStyle={{ padding: isMobile ? 16 : '36px 40px 32px' }}>
                <Flex
                  className="w-100 m-auto"
                  vertical
                  align={isMobile ? 'center' : 'normal'}
                  style={{ maxWidth: isMobile ? '560px' : '860px' }}
                >
                  <ChooserHeader $isMobile={isMobile}>
                    {isMobile ? <h6>{t('Post a Listing')}</h6> : <h1>{t('Post a Listing')}</h1>}
                    <ChooserSubtitle $isMobile={isMobile}>
                      {t('Choose the type of listing you want to publish')}
                    </ChooserSubtitle>
                  </ChooserHeader>
                  <ChooserGrid $isMobile={isMobile}>{options.map(renderOptionCard)}</ChooserGrid>
                </Flex>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row style={{ marginBlockStart: '20px' }}>
            <Col xs={24} lg={18} xxl={15} style={{ margin: 'auto' }}>
              <Button
                size="small"
                className="mb-8"
                style={{ border: 0 }}
                type="primaryOutlined"
                icon="IoMdArrowRoundBack"
                onClick={() => navigate(-1)}
                iconSize="14px"
              >
                {' '}
                {t('Back')}
              </Button>
              <Card bodyStyle={{ padding: isMobile ? 16 : 40 }}>
                <Flex
                  className="w-100 m-auto"
                  vertical
                  align="center"
                  justify={!isMobile ? 'center' : null}
                  style={{ minHeight: '410px', maxWidth: '560px' }}
                >
                  <div className={cx(isMobile ? 'mb-20' : 'mb-40', 'text-center')}>
                    <Heading
                      as={isMobile ? 'h6' : null}
                      className={isMobile ? 'mb-0' : 'mb-8'}
                      style={{ fontWeight: '700' }}
                    >
                      {t('Post a Listing')}
                    </Heading>
                    <Text className={isMobile ? 'fz-12' : 'fz-16'} type="secondary">
                      {t('Choose the type of listing you want to publish')}
                    </Text>
                  </div>
                  {/* Make a common component for type selection */}
                  <Flex vertical={isMobile && true} className="mb-16" gap="20px">
                    {cardList.map((item) =>
                      item?.id == 2 ? (
                        <Badge.Ribbon key={item.id} text={t('NEW')} color="red" placement="end">
                          {renderPropertyCards(item)}
                        </Badge.Ribbon>
                      ) : (
                        renderPropertyCards(item)
                      ),
                    )}
                  </Flex>

                  <Button
                    className="w-100"
                    onClick={handleClickOnListingSelection}
                    loading={loading}
                    type={'primary'}
                    size={'large'}
                    disabled={user?.is_nafaz_verified && !user?.is_saudi_national}
                  >
                    {t('Continue')}
                  </Button>
                </Flex>
              </Card>
            </Col>
          </Row>
        )
      ) : showPermit ? (
        <PermitValidation postNewListing={postNewListings} setShowPermitField={handleSetShowPermitField} />
      ) : (
        <TenantComponents.LicenseValidation
          postNewListing={postNewListings}
          setShowAdLicenseField={handleSetShowAdLicenseField}
        />
      )}
      {isFalOtpVerificationEnabled && (
        <OtpVerificationModal
          ref={otpVerifyRef}
          value={otpPhoneNumber}
          okText={t('Verify')}
          onSuccess={() => {
            setOtpPhoneNumber(null);
            if (otpListingId) navigate(`${tenantRoutes.app().post_listing.path}/${otpListingId}`);
          }}
          onCancelModal={() => {
            setLoading(false);
            setOtpPhoneNumber(null);
            if (hasRateLimitError) {
              navigate(tenantRoutes.app().listings.path);
            }
          }}
          onFail={() => {
            setLoading(false);
          }}
          customVerifyFunction={async (_val, otpCode) => {
            const res = await verifyLicenseOtp({ otp: otpCode, reference_number: otpReferenceNumber });
            if (res?.error) {
              if (res?.error?.status === 429) {
                setHasRateLimitError(true);
              }
              return { error: res?.error?.message };
            }
            return res;
          }}
          customResendFunction={async () => {
            const res = await sendLicenseOtp({
              listing_id: otpListingId,
              user_id: user?.id,
              identifier: otpPhoneNumber,
            });
            if (!res?.error) {
              setOtpReferenceNumber(res?.data?.reference_number);
            }
            return res;
          }}
          disableActions={hasRateLimitError}
        />
      )}
    </>
  );
};
export default PostListingWithPropertyType;
